/**
 * React context surface of the theme system: `useTheme()` and `ThemeProvider`.
 *
 * The provider merges its `theme` prop — one layer, or an array of layers
 * composed in order — over the parent context value (nested providers
 * compose), memoizes the result, and publishes it twice from the same object
 * so the two surfaces cannot drift: as React context for `useTheme()`
 * consumers, and as CSS custom properties via `ThemeScope` for CSS Modules.
 * It also loads the font group its own layers name, so the faces a theme
 * names arrive wherever that theme takes effect. Rendering without a
 * provider falls back to `defaultTheme` — the context default.
 */

import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';

import { ThemeScope } from './scope';
import { foldLayers, mergeTheme } from './theme.helpers';
import type { Theme, ThemeLayer } from './tokens';
import { defaultTheme } from './tokens';

const ThemeContext = createContext<Theme>(defaultTheme);

/**
 * Returns the resolved design tokens from the nearest `ThemeProvider`, or
 * `defaultTheme` when rendered without one.
 * @public
 */
export function useTheme(): Theme {
    return useContext(ThemeContext);
}

/**
 * Lifts the theme prop into a layer array and keeps it referentially stable:
 * when a render produces a new array whose elements all match the last one,
 * the previous array is returned instead, so `theme={[base, brand]}`
 * written inline does not recompose the theme (or re-render the subtree)
 * every render. The ref write during render is an idempotent last-seen cache.
 */
function useStableLayers(theme: ThemeLayer | ThemeLayer[]): ThemeLayer[] {
    const incoming = Array.isArray(theme) ? theme : [theme];
    const previousLayersRef = useRef<ThemeLayer[]>(incoming);
    const previousLayers = previousLayersRef.current;
    if (previousLayers.length === incoming.length && incoming.every((layer, i) => layer === previousLayers[i])) {
        return previousLayers;
    }
    previousLayersRef.current = incoming;
    return incoming;
}

/**
 * Publishes a theme to the subtree. `theme` is a layer or an array of layers,
 * composed left to right and merged over the parent provider's tokens (or
 * `defaultTheme` at the outermost provider); falsy array entries are skipped,
 * so layers can be included conditionally. The result is readable via
 * `useTheme()` and, on web, as CSS custom properties on an invisible
 * `display: contents` wrapper. It also loads the font group its own layers
 * name, so callers never wire font loading themselves; a provider whose
 * layers name no fonts loads nothing and uses the fonts already loaded
 * above it. The provider never reads `window` — resolving which theme
 * applies is the caller's concern.
 * @public
 */
export function ThemeProvider({ theme, children }: { theme?: ThemeLayer | ThemeLayer[]; children: ReactNode }) {
    const parent = useContext(ThemeContext);
    const layers = useStableLayers(theme);
    const tokens = useMemo(() => mergeTheme(parent, ...layers), [parent, layers]);
    const loadFontsAsync = useMemo(() => foldLayers(layers).fonts?.load, [layers]);

    useEffect(() => {
        loadFontsAsync?.().catch((error) => console.error('Theme font loading failed', error));
    }, [loadFontsAsync]);

    return (
        <ThemeContext.Provider value={tokens}>
            <ThemeScope tokens={tokens}>{children}</ThemeScope>
        </ThemeContext.Provider>
    );
}
